import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import type { ComponentType } from 'react';

import { cn } from '@/lib/utils';
import { useToastStore, type ToastTone } from '@/stores/toast-store';

const TONE_ICONS: Record<ToastTone, ComponentType<{ className?: string }>> = {
  default: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
};

const TONE_STYLES: Record<ToastTone, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

/**
 * The toast viewport.
 *
 * Mounted once, at the root. Radix owns the accessibility contract here: the viewport
 * is an `aria-live` region, and its swipe/escape handling and focus management are the
 * parts that are genuinely hard to get right by hand.
 *
 * Positioned bottom-right on desktop and bottom-centre on mobile, above the safe-area
 * inset so it clears a home indicator.
 */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4500}>
      {toasts.map((item) => {
        const Icon = TONE_ICONS[item.tone];

        return (
          <ToastPrimitive.Root
            key={item.id}
            duration={Number.isFinite(item.duration) ? item.duration : undefined}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id);
            }}
            className={cn(
              'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
              'rounded-lg border border-border bg-card p-3.5 pr-10 shadow-lg',
              'data-[state=open]:animate-slide-in-right data-[state=closed]:animate-fade-out',
              // Track the finger during a swipe, then animate out from where it was let go.
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
              'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
              'data-[swipe=end]:animate-slide-out-right',
            )}
          >
            <Icon className={cn('mt-0.5 size-4 shrink-0', TONE_STYLES[item.tone])} aria-hidden="true" />

            <div className="min-w-0 flex-1 space-y-0.5">
              <ToastPrimitive.Title className="text-base font-medium leading-snug">
                {item.title}
              </ToastPrimitive.Title>
              {item.description ? (
                <ToastPrimitive.Description className="break-words text-sm leading-relaxed text-muted-foreground">
                  {item.description}
                </ToastPrimitive.Description>
              ) : null}
            </div>

            <ToastPrimitive.Close
              aria-label="Dismiss notification"
              className={cn(
                'absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-md',
                'text-muted-foreground opacity-0 transition-opacity duration-fast',
                'hover:bg-muted hover:text-foreground',
                // Always reachable by keyboard even before the pointer arrives.
                'focus-visible:opacity-100 group-hover:opacity-100',
              )}
            >
              <X className="size-3.5" aria-hidden="true" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}

      <ToastPrimitive.Viewport
        className={cn(
          'pointer-events-none fixed z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4',
          'bottom-[env(safe-area-inset-bottom)] left-0 right-0',
          'sm:left-auto sm:bottom-0 sm:max-w-sm sm:flex-col',
        )}
      />
    </ToastPrimitive.Provider>
  );
}
